import { Repository, SearchFilters } from './repository';
import { LocalStorageRepository } from './repositories/local/index';
import { D1Repository } from './repositories/d1/index';
import { features, FeatureFlags } from './features';
import { isD1Configured } from './d1';
import { Listing, Review, BlogPost, UserProfile, AnalyticsData, ListingStatus, ApiResult, Vendor } from '../types';

class DatabaseService implements Repository {
  private localRepo: LocalStorageRepository;
  private d1Repo: D1Repository;

  constructor() {
    this.localRepo = new LocalStorageRepository();
    this.d1Repo = new D1Repository();
  }

  private repo(feature: keyof FeatureFlags): Repository {
      if (import.meta.env.PROD && !isD1Configured()) {
          throw new Error(`CRITICAL: Backend not configured in production. Local fallback is disabled for ${String(feature)}.`);
      }
      return features.get()[feature] ? this.d1Repo : this.localRepo;
  }

  // Delegate all calls to the underlying repo

  // --- Listings ---

  getListingById(id: string): Promise<Listing | undefined> {
    return this.repo('useD1Listings').getListingById(id);
  }

  getListings(filters?: SearchFilters): Promise<Listing[]> {
    return this.repo('useD1Listings').getListings(filters);
  }

  getListingsCount(): Promise<number | null> {
    return this.repo('useD1Listings').getListingsCount();
  }

  getHomepageListings(): Promise<{ listings: Listing[]; totalCount: number | null }> {
    return this.repo('useD1Listings').getHomepageListings();
  }

  getAdminListings(): Promise<Listing[]> {
    return this.repo('useD1Listings').getAdminListings();
  }

  getMyListings(userId: string): Promise<Listing[]> {
    return this.repo('useD1Listings').getMyListings(userId);
  }

  addListing(listing: Listing, userId: string): Promise<ApiResult<Listing>> {
    return this.repo('useD1Listings').addListing(listing, userId);
  }

  updateListing(listing: Listing, userId: string): Promise<ApiResult<Listing>> {
    return this.repo('useD1Listings').updateListing(listing, userId);
  }

  updateListingStatus(id: string, status: ListingStatus): Promise<boolean> {
    return this.repo('useD1Listings').updateListingStatus(id, status);
  }

  /** Admin-only: update provenance/trust columns of a listing. */
  updateListingTrust(id: string, fields: Record<string, string | boolean | null>): Promise<boolean> {
    return this.repo('useD1Listings').updateListingTrust(id, fields);
  }

  updateListingImages(id: string, images: string[]): Promise<boolean> {
    return this.repo('useD1Listings').updateListingImages(id, images);
  }

  deleteListing(id: string): Promise<ApiResult<void>> {
    return this.repo('useD1Listings').deleteListing(id);
  }

  // --- Vendors ---

  getVendorById(id: string): Promise<Vendor | undefined> {
    return this.repo('useD1Vendors').getVendorById(id);
  }

  getVendors(filters?: any): Promise<Vendor[]> {
    return this.repo('useD1Vendors').getVendors(filters);
  }

  getAdminVendors(): Promise<Vendor[]> {
    return this.repo('useD1Vendors').getVendors({ admin: true, status: 'all' });
  }

  getMyVendors(userId: string): Promise<Vendor[]> {
    return this.repo('useD1Vendors').getMyVendors(userId);
  }

  addVendor(vendor: Vendor, userId: string): Promise<ApiResult<Vendor>> {
    return this.repo('useD1Vendors').addVendor(vendor, userId);
  }

  updateVendor(vendor: Vendor, userId: string): Promise<ApiResult<Vendor>> {
    return this.repo('useD1Vendors').updateVendor(vendor, userId);
  }

  deleteVendor(id: string): Promise<ApiResult<void>> {
    return this.repo('useD1Vendors').deleteVendor(id);
  }

  // --- Reviews ---

  getReviewsForListing(listingId: string): Promise<Review[]> {
    return this.repo('useD1Reviews').getReviewsForListing(listingId);
  }

  getMyReviewForListing(userId: string, listingId: string): Promise<Review | null> {
    return this.repo('useD1Reviews').getMyReviewForListing(userId, listingId);
  }

  canReviewListing(userId: string, listingId: string): Promise<boolean> {
    return this.repo('useD1Reviews').canReviewListing(userId, listingId);
  }

  createReview(params: Omit<Review, 'id' | 'createdAt'>): Promise<ApiResult<Review>> {
    return this.repo('useD1Reviews').createReview(params);
  }


  // --- Favorites ---

  isFavorite(userId: string, listingId: string): Promise<boolean> {
    return this.repo('useD1Listings').isFavorite(userId, listingId);
  }

  addFavorite(userId: string, listingId: string): Promise<boolean> {
    return this.repo('useD1Listings').addFavorite(userId, listingId);
  }

  removeFavorite(userId: string, listingId: string): Promise<boolean> {
    return this.repo('useD1Listings').removeFavorite(userId, listingId);
  }

  getFavorites(userId: string): Promise<string[]> {
    return this.repo('useD1Listings').getFavorites(userId);
  }

  getFavoriteListings(userId: string): Promise<Listing[]> {
    return this.repo('useD1Listings').getFavoriteListings(userId);
  }

  // --- User Profile (authentication itself lives in Clerk, see useAuth) ---

  async getUserProfile(): Promise<UserProfile | null> {
    return this.repo('useD1Auth').getUserProfile();
  }

  async updateUserProfile(profile: Partial<UserProfile>): Promise<UserProfile> {
    return this.repo('useD1Auth').updateUserProfile(profile);
  }

  getAllUserProfiles(): Promise<UserProfile[]> {
    return this.repo('useD1Auth').getAllUserProfiles();
  }

  deleteUserListings(userId: string): Promise<number> {
    return this.repo('useD1Listings').deleteUserListings(userId);
  }

  deleteAccountData(userId: string): Promise<boolean> {
    return this.repo('useD1Listings').deleteAccountData(userId);
  }

  // --- Analytics ---

  getListingAnalytics(id: string, days?: number): Promise<AnalyticsData[]> {
    return this.repo('useD1Analytics').getListingAnalytics(id, days);
  }

  getListingsAnalyticsBulk(ids: string[], days?: number): Promise<AnalyticsData[]> {
    return this.repo('useD1Analytics').getListingsAnalyticsBulk(ids, days);
  }

  trackListingClick(listingId: string): Promise<void> {
    return this.repo('useD1Analytics').trackListingClick(listingId);
  }

  // --- Blog ---

  getBlogPosts(): Promise<BlogPost[]> {
    return this.repo('useD1Blog').getBlogPosts();
  }

  getBlogPost(id: number): Promise<BlogPost | undefined> {
    return this.repo('useD1Blog').getBlogPost(id);
  }

  // Helper for determining mode (legacy compatibility)
  public get useD1(): boolean { return features.get().useD1Listings; }
  public canUseD1(): boolean { return this.useD1; }

  toggleListingStatus(listingId: string, currentStatus: string): Promise<{ success: boolean; error?: string }> {
    return this.repo('useD1Listings').toggleListingStatus(listingId, currentStatus);
  }

  updateListingProfileRequirement(sellerId: string, required: boolean): Promise<{ success: boolean; error?: string }> {
    return this.repo('useD1Listings').updateListingProfileRequirement(sellerId, required);
  }

  // --- Settings ---
  getSetting(key: string): Promise<string | null> {
    return this.repo('useD1Listings').getSetting(key);
  }

  setSetting(key: string, value: any): Promise<boolean> {
    return this.repo('useD1Listings').setSetting(key, value);
  }

  syncAuth(data: { email?: string; name?: string; notify_on_status_change?: boolean }): Promise<void> {
    return this.repo('useD1Auth').syncAuth(data);
  }
}

export const db = new DatabaseService();
